/*
 * EB Gift Tray — panel visible con los regalos disponibles/bloqueados.
 * Renderizado inline dentro de cart-summary.liquid (vive en cart drawer y /cart page).
 * `data-skip-subtree-update` impide que la morphSection borre el contenido renderizado.
 */
(function () {
  if (customElements.get('eb-gift-tray')) return;

  class EBGiftTray extends HTMLElement {
    connectedCallback() {
      this._unsubCart = null;
      // Debounce + dedupe: Pitch dispara varios cart:update por cada cambio
      // (one-by-one removal, morph, push) y cada uno triggea render. Antes parpadeaba.
      // Solución doble:
      //  1. Coalescemos llamadas en una ventana de 80ms (timeout, no rAF — los
      //     updates llegan en frames distintos).
      //  2. Cacheamos el HTML de salida; si el render produce el mismo string
      //     que el anterior, NO tocamos el DOM (evita flash visual aunque la
      //     lógica de render sí corra).
      this._renderTimer = null;
      this._lastRenderedHTML = null;
      const scheduleRender = () => {
        if (this._renderTimer) return;
        this._renderTimer = setTimeout(() => {
          this._renderTimer = null;
          this.render();
        }, 80);
      };
      this._boundUpdate = scheduleRender;
      this._boundStateChange = scheduleRender;

      document.addEventListener('eb-gift:state-change', this._boundStateChange);
      this._boundToast = (e) => this.showToast(e?.detail?.tier);
      document.addEventListener('eb-gift:added', this._boundToast);

      const start = () => {
        if (!window.EBCart) return setTimeout(start, 80);
        this._unsubCart = window.EBCart.subscribe(this._boundUpdate);
      };
      start();

      this.render();

      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-eb-gift-toggle]');
        if (!btn) return;
        const tier = btn.dataset.tier;
        const action = btn.dataset.action;
        if (action === 'remove') {
          window.EBGiftReconciler?.setOptedOut(tier, true);
          window.EBGiftReconciler?.reconcile();
        } else if (action === 'add') {
          window.EBGiftReconciler?.setOptedOut(tier, false);
          window.EBGiftReconciler?.reconcile();
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('eb-gift:state-change', this._boundStateChange);
      document.removeEventListener('eb-gift:added', this._boundToast);
      if (this._unsubCart) this._unsubCart();
    }

    showToast(tier) {
      if (!tier) return;
      const labels = {
        t1: 'unas muestras de regalo',
        t2: 'un peine de regalo',
        t3: 'un emulsionador de regalo',
      };
      const label = labels[tier] || 'un regalo';
      let toast = document.querySelector('.eb-gift-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'eb-gift-toast';
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
      }
      // Attach inside dialog when drawer is open so the toast appears in the
      // top-layer stacking context (modern <dialog>:modal otherwise paints over body-level toasts).
      const dialog = this._dialog || (this._drawer && this._drawer.querySelector('dialog'));
      const drawerOpen = !!(dialog && dialog.open);
      const targetParent = drawerOpen ? dialog : document.body;
      if (toast.parentElement !== targetParent) {
        try { targetParent.appendChild(toast); } catch (_e) {}
      }
      toast.innerHTML = '<span aria-hidden="true">🎁</span> Te hemos añadido <strong>' + label + '</strong> con tu pedido';
      toast.classList.remove('is-out');
      void toast.offsetWidth;
      toast.classList.add('is-in');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        toast.classList.remove('is-in');
        toast.classList.add('is-out');
      }, 3600);
    }

    money(amount, currency) {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency', currency: currency || 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(amount);
    }

    render() {
      if (!window.EBGiftReconciler) { this._setHTML(''); return; }
      const { config, realSubtotal, items } = window.EBGiftReconciler.getState();
      if (!config || !config.tiers) { this._setHTML(''); return; }

      const tierNames = {
        t1: this.dataset.t1Name || 'Muestras',
        t2: this.dataset.t2Name || 'Peine de regalo',
        t3: this.dataset.t3Name || 'Emulsionador',
      };

      const giftByTier = {};
      for (const it of items || []) {
        const tier = it.properties && it.properties._es_regalo;
        if (tier && !giftByTier[tier]) giftByTier[tier] = it;
      }

      const anyConfigured = config.tiers.some((t) => t.variantId);
      if (!anyConfigured || (items.length === 0 && realSubtotal === 0)) {
        this._setHTML('');
        return;
      }

      // Detectar pack-builder activo: gifts y pack -10% son mutuamente
      // excluyentes (limitación de Shopify Functions). Si hay pack, NO mostrar
      // los regalos como "reclamables" — sería engañoso porque no se aplicarán.
      // En su lugar, render informativo (sin botones).
      const packActive = (function () {
        const counts = {};
        for (const it of items || []) {
          const grp = it.properties && it.properties._pack_grupo;
          if (!grp) continue;
          counts[grp] = (counts[grp] || 0) + 1;
        }
        for (const k in counts) { if (counts[k] >= 4) return true; }
        return false;
      })();

      if (packActive) {
        const title = this.dataset.title || 'Tus regalos';
        this._setHTML(`
          <div class="eb-gifts__card eb-gifts__card--pack-active">
            <div class="eb-gifts__head">
              <span class="eb-gifts__title">${title}</span>
            </div>
            <p class="eb-gifts__pack-note">
              Tu <strong>pack personalizado</strong> ya tiene <strong>−10%</strong> aplicado.
              Los regalos progresivos no se acumulan con descuentos de pack.
            </p>
            <p class="eb-gifts__pack-note eb-gifts__pack-note--small">
              Si prefieres los regalos en lugar del pack, deshaz el pack desde el carrito.
            </p>
          </div>
        `);
        return;
      }

      // Build tier rows
      const rows = config.tiers.map((tier) => {
        if (!tier.variantId) return '';
        const name = tierNames[tier.id] || 'Regalo';
        const threshold = tier.threshold;
        const gift = giftByTier[tier.id];
        const optedOut = window.EBGiftReconciler.isOptedOut(tier.id);
        const unlocked = realSubtotal >= threshold;

        if (!unlocked) {
          const missing = Math.max(0, threshold - realSubtotal);
          return `
            <li class="eb-gifts__row eb-gifts__row--locked" data-tier="${tier.id}">
              <span class="eb-gifts__icon" aria-hidden="true">🔒</span>
              <span class="eb-gifts__body">
                <span class="eb-gifts__name">${name}</span>
                <span class="eb-gifts__meta">Añade ${this.money(missing)} más para desbloquearlo</span>
              </span>
            </li>`;
        }

        if (gift && !optedOut) {
          return `
            <li class="eb-gifts__row eb-gifts__row--active" data-tier="${tier.id}">
              <span class="eb-gifts__icon" aria-hidden="true">✓</span>
              <span class="eb-gifts__body">
                <span class="eb-gifts__name">${name}</span>
                <span class="eb-gifts__meta eb-gifts__meta--free">Regalo incluido</span>
              </span>
              <button type="button" class="eb-gifts__btn eb-gifts__btn--remove" data-eb-gift-toggle data-tier="${tier.id}" data-action="remove" aria-label="Quitar ${name} del pedido">No, gracias</button>
            </li>`;
        }

        // Unlocked pero opt-out o no añadido aún
        return `
          <li class="eb-gifts__row eb-gifts__row--available" data-tier="${tier.id}">
            <span class="eb-gifts__icon" aria-hidden="true">🎁</span>
            <span class="eb-gifts__body">
              <span class="eb-gifts__name">${name}</span>
              <span class="eb-gifts__meta">Disponible gratis · lo puedes añadir</span>
            </span>
            <button type="button" class="eb-gifts__btn eb-gifts__btn--add" data-eb-gift-toggle data-tier="${tier.id}" data-action="add" aria-label="Añadir ${name} al pedido">Añadir</button>
          </li>`;
      }).join('');

      const title = this.dataset.title || 'Tus regalos';

      this._setHTML(`
        <div class="eb-gifts__card">
          <div class="eb-gifts__head">
            <span class="eb-gifts__title">${title}</span>
          </div>
          <ul class="eb-gifts__list">${rows}</ul>
        </div>
      `);
    }

    /** Sólo toca el DOM si el HTML cambió respecto al render anterior. */
    _setHTML(html) {
      if (this._lastRenderedHTML === html) return;
      this._lastRenderedHTML = html;
      this.innerHTML = html;
    }
  }

  customElements.define('eb-gift-tray', EBGiftTray);
})();
